import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { VerifyEmailBanner } from './VerifyEmailBanner';
import { LogOut, User, Shield, LayoutGrid, Users as UsersIcon, Car, Menu, X, Globe, DollarSign, CalendarRange, ShoppingCart, Settings, Monitor } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Header() {
  const { user, logout, isAdmin } = useAuth();
  const { t, language, setLanguage, languages } = useLanguage();
  const [cartCount, setCartCount] = useState(0);

  const getRoleBadge = (role) => {
    if (role === 'admin') return { cls: 'bg-yellow-100 text-yellow-700', label: t('admin') };
    if (role === 'interno') return { cls: 'bg-emerald-100 text-emerald-700', label: t('interno') };
    return { cls: 'bg-blue-100 text-blue-700', label: t('coworker') };
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  // Fetch cart count
  useEffect(() => {
    if (!isAdmin) {
      axios.get(`${API_URL}/api/cart`, { withCredentials: true })
        .then(({ data }) => setCartCount(data.item_count || 0))
        .catch(() => {});
    }
  }, [location.pathname, isAdmin]);

  const navItems = [
    { path: '/dashboard', labelKey: 'desks', icon: LayoutGrid, testId: 'nav-desks' },
    { path: '/meeting-room', labelKey: 'meetingRoom', icon: UsersIcon, testId: 'nav-meeting-room' },
    { path: '/parking', labelKey: 'parking', icon: Car, testId: 'nav-parking' },
    ...(isAdmin ? [
      { path: '/pricing', labelKey: 'pricing', icon: DollarSign, testId: 'nav-pricing' },
      { path: '/long-period-requests', labelKey: 'longPeriodRequests', icon: CalendarRange, testId: 'nav-long-period' },
      { path: '/admin/users', labelKey: 'users', icon: UsersIcon, testId: 'nav-admin-users' },
      { path: '/admin/desks', labelKey: 'manageDesks', icon: Settings, testId: 'nav-admin-desks' },
    ] : []),
  ];

  return (
    <>
      <header className="sticky top-2 mx-2 md:mx-4 rounded-2xl md:rounded-full bg-white/90 backdrop-blur-xl shadow-sm border border-slate-100 z-50 px-3 md:px-6 py-2 md:py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-sm md:text-lg">CW</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-sm md:text-lg font-bold text-slate-900">{t('systemName')}</h1>
              <p className="text-xs text-slate-500 hidden md:block">{t('systemSubtitle')}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.path}
                variant={location.pathname === item.path ? 'default' : 'ghost'}
                size="sm"
                onClick={() => navigate(item.path)}
                data-testid={item.testId}
                className={`rounded-full ${location.pathname === item.path ? 'bg-blue-500 text-white' : ''}`}
              >
                <item.icon className="w-4 h-4 mr-2" />
                {t(item.labelKey)}
              </Button>
            ))}
          </nav>

          {/* Desktop User Info + Language */}
          <div className="hidden md:flex items-center gap-3">
            {/* Cart (non-admin) */}
            {!isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/cart')}
                data-testid="cart-btn"
                className="rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2 gap-1 relative"
              >
                <ShoppingCart className="w-4 h-4" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Button>
            )}

            {/* Language Switcher */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLangOpen(!langOpen)}
                data-testid="language-switcher"
                className="rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2 gap-1"
              >
                <Globe className="w-4 h-4" />
                <span className="text-xs font-bold">{language.toUpperCase()}</span>
              </Button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white rounded-xl shadow-lg border border-slate-100 py-1 min-w-[100px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                      data-testid={`lang-${lang.code}`}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 ${
                        language === lang.code ? 'font-bold text-blue-600' : 'text-slate-700'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm">
              {isAdmin ? (
                <Shield className="w-4 h-4 text-yellow-500" />
              ) : (
                <User className="w-4 h-4 text-blue-500" />
              )}
              <span className="font-semibold text-slate-700">{user?.name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${getRoleBadge(user?.role).cls}`}>
                {getRoleBadge(user?.role).label}
              </span>
            </div>
            
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              data-testid="logout-btn"
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('logout')}
            </Button>
          </div>

          {/* Mobile: Cart + Language + Menu */}
          <div className="flex items-center gap-1 md:hidden">
            {/* Mobile Cart */}
            {!isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/cart')} data-testid="mobile-cart-btn" className="px-2 relative">
                <ShoppingCart className="w-4 h-4" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-blue-500 text-white text-[9px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Button>
            )}
            {/* Mobile Language Switcher */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLangOpen(!langOpen)}
                data-testid="mobile-language-switcher"
                className="px-2"
              >
                <Globe className="w-4 h-4" />
                <span className="text-xs font-bold ml-0.5">{language.toUpperCase()}</span>
              </Button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white rounded-xl shadow-lg border border-slate-100 py-1 min-w-[80px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                      data-testid={`mobile-lang-${lang.code}`}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 ${
                        language === lang.code ? 'font-bold text-blue-600' : 'text-slate-700'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <VerifyEmailBanner />

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-16 left-2 right-2 bg-white rounded-2xl shadow-xl p-4 animate-fade-in-up">
            <nav className="space-y-2 mb-4">
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  variant={location.pathname === item.path ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  data-testid={`mobile-${item.testId}`}
                  className={`w-full justify-start rounded-xl ${location.pathname === item.path ? 'bg-blue-500 text-white' : ''}`}
                >
                  <item.icon className="w-4 h-4 mr-3" />
                  {t(item.labelKey)}
                </Button>
              ))}
            </nav>

            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-3">
                {isAdmin ? (
                  <Shield className="w-4 h-4 text-yellow-500" />
                ) : (
                  <User className="w-4 h-4 text-blue-500" />
                )}
                <span className="font-semibold text-slate-700">{user?.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                  user?.role === 'admin' ? 'bg-yellow-100 text-yellow-700'
                  : user?.role === 'interno' ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-blue-100 text-blue-700'
                }`}>
                  {user?.role === 'admin' ? t('admin') : user?.role === 'interno' ? t('interno') : t('coworker')}
                </span>
              </div>
              <Button
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                variant="outline"
                size="sm"
                className="w-full rounded-xl"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
